import React, { useContext, useEffect, useRef } from "react";
import Navbar from "../components/Navbar/Navbar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../store/auth-context";
import Sidebar from "../components/Sidebar/Sidebar";
import SkipLink from "../components/SkipLink/SkipLink";
import usePageBottom from "../hooks/usePageBottom";
import useRouteFocus from "../hooks/useRouteFocus";
import { automationTestIds } from "../testing/automationTestIds";
import { useTranslation } from "react-i18next";
import { isOnboardingSetupPath } from "../routes/onboardingAccess";

const AuthLayout = () => {
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  const customElementRef = useRef(null);
  const mainContentRef = useRef(null);
  useRouteFocus(mainContentRef);
  const reachedBottom = usePageBottom(
    { rootMargin: 0, threshold: 0.5 },
    customElementRef,
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    authContext.hasDiscoveryReachedBottomHandler(reachedBottom);
  }, [reachedBottom]);

  useEffect(() => {
    if (authContext.isLoading) {
      return;
    }

    if (!authContext.isLoggedIn) {
      // Save the current path to redirect after login only if it's an issuer route
      if (location.pathname.startsWith("/issuer/")) {
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/");
      }
    }
  }, [
    authContext.isLoading,
    authContext.isLoggedIn,
    location.pathname,
    navigate,
  ]);

  if (authContext.isLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary flex items-center justify-center">
        {t("workspace:loading")}
      </div>
    );
  }

  if (!authContext.isLoggedIn) {
    return null;
  }

  const shouldShowSidebar =
    authContext.isNewUserOnboarding !== true &&
    !isOnboardingSetupPath(location.pathname, location.search);

  return (
    <React.Fragment>
      <SkipLink />
      <div
        id={automationTestIds.layoutNavbarWrapper}
        className="fixed top-0 w-full z-10"
      >
        <Navbar />
      </div>
      <div className="overflow-y-hidden">
        {shouldShowSidebar ? (
          <aside
            id={automationTestIds.layoutSidebarAside}
            className="fixed top-[5.1rem] left-0 w-fit h-[calc(100vh-5.1rem)] z-10"
            aria-label="Sidebar"
          >
            <div className="h-full overflow-hidden">
              <Sidebar />
            </div>
          </aside>
        ) : null}
        <div
          id={automationTestIds.layoutAuthMain}
          className={`mb-[4rem] sm:!mb-[0rem] ml-0 overflow-auto ${
            shouldShowSidebar ? "sm:!ml-[5.7rem]" : ""
          }`}
        >
          <main
            id="main-content"
            ref={mainContentRef}
            tabIndex={-1}
            className="outline-none focus-visible:outline-none"
          >
            {authContext.isLoggedIn && <Outlet />}
          </main>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AuthLayout;
